import { useState } from 'react';
import { Check, Circle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import type { ChecklistTally, SheetLead } from '../../../hooks/useLeadSheet';
import { dateLabel } from '../../../lib/crm/opportunitySheet';
import { leadFicheHref, leadStatusLabel, leadTypeLabel, type LeadOnboarding } from '../../../lib/crm/leadSheet';
import { ACTIVE_ONBOARDING_STAGES, onboardingStageFraction, onboardingStageName, STABLE_ONBOARDING_STAGE } from '../../../lib/onboardingStage';
import { BTN_GHOST, BTN_PRIMARY, BTN_SECONDARY } from '../../bureau/buttons';
import { FIELD } from '../../bureau/encre';
import { SheetBlock } from './EncreSheet';

const REASON_MIN = 20;

function Bar({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div role="progressbar" aria-label={label} aria-valuenow={value} aria-valuemin={0} aria-valuemax={max} className="h-1.5 w-full rounded-full bg-desk-subtle">
      <div className="h-1.5 rounded-full bg-desk-accent" style={{ width: `${pct}%` }} />
    </div>
  );
}

function Tally({ label, tally }: { label: string; tally: ChecklistTally }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="mb-1 flex justify-between text-xs text-desk-soft">
        <span>{label}</span>
        <span>
          {tally.done}/{tally.total}
        </span>
      </p>
      <Bar value={tally.done} max={tally.total} label={`${label} : ${tally.done} sur ${tally.total}`} />
    </div>
  );
}

/** Going back to an earlier stage: which one, and why (the back asks 20 characters or more). */
function Reopen({ stage, busy, onReopen, onCancel }: { stage: number; busy: boolean; onReopen: (to: number, reason: string) => void; onCancel: () => void }) {
  const [to, setTo] = useState(stage - 1);
  const [reason, setReason] = useState('');
  const ready = reason.trim().length >= REASON_MIN && !busy;
  return (
    <div className="mt-3 rounded-2xl bg-desk-card p-4 shadow-lift">
      <label className="block">
        <span className="mb-1.5 block text-xs font-medium text-desk-soft">Revenir à l'étape</span>
        {/* The form takes the focus: the gesture that opened it is gone. */}
        <select autoFocus className={FIELD} value={to} onChange={(e) => setTo(Number(e.target.value))}>
          {Array.from({ length: stage - 1 }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {onboardingStageFraction(n)} · {onboardingStageName(n)}
            </option>
          ))}
        </select>
      </label>
      <label className="mt-3 block">
        <span className="mb-1.5 block text-xs font-medium text-desk-soft">Pourquoi (au moins {REASON_MIN} caractères)</span>
        <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className={twMerge(FIELD, 'h-auto resize-none py-2')} />
      </label>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" disabled={!ready} onClick={() => onReopen(to, reason)} className={BTN_PRIMARY}>
          {busy && <Loader2 size={15} className="animate-spin" aria-hidden="true" />} Revenir à cette étape
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={BTN_GHOST}>
          Annuler
        </button>
      </div>
    </div>
  );
}

/**
 * « Onboarding »: where the client stands — its stage as the back reads it, since when, the stage's
 * exit criteria met or not —, its status, type and plan, its checklists; then moving it: the next
 * stage (the back checks the criteria and says the missing ones) or back to an earlier one, with why.
 */
export function LeadOnboardingBlock({
  lead,
  onboarding,
  checklists,
  busy,
  onAdvance,
  onReopen,
  onNavigate,
}: {
  lead: SheetLead;
  onboarding: LeadOnboarding | null;
  checklists: { client: ChecklistTally | null; internal: ChecklistTally | null };
  busy: boolean;
  onAdvance: (expectedStage: number) => void;
  onReopen: (to: number, reason: string) => Promise<boolean>;
  onNavigate: () => void;
}) {
  const [reopening, setReopening] = useState(false);
  const row = lead.row;
  const stage = onboarding?.stage ?? row.current_onboarding_stage ?? 0;
  const name = onboarding?.stageName || onboardingStageName(stage);
  const since = dateLabel(onboarding?.since);
  const stageText = stage <= 0 ? 'Pas commencé' : stage >= STABLE_ONBOARDING_STAGE ? 'Stable · onboarding terminé' : `${onboardingStageFraction(stage)} · ${name}`;
  const facts: [string, string | null][] = [
    ['Statut', leadStatusLabel(row.status)],
    ['Type', leadTypeLabel(row.onboarding_type)],
    ['Plan', row.selected_plan],
    ['Créé', dateLabel(row.created_at)],
  ];
  return (
    <SheetBlock title="Onboarding">
      <p className="text-sm font-medium text-desk-ink">
        {stageText}
        {since && stage > 0 && <span className="font-normal text-desk-soft"> · depuis le {since}</span>}
      </p>
      {stage > 0 && stage < STABLE_ONBOARDING_STAGE && (
        <div className="mt-2">
          <Bar value={stage} max={ACTIVE_ONBOARDING_STAGES} label={`Étape ${stage} sur ${ACTIVE_ONBOARDING_STAGES}`} />
        </div>
      )}
      {/* Not started: the back gives stage 1's criteria, which starting does not need. */}
      {onboarding && onboarding.criteria.length > 0 && stage >= 1 && stage < STABLE_ONBOARDING_STAGE && (
        <ul className="m-0 mt-3 list-none space-y-1 p-0" aria-label="Critères de sortie de l'étape">
          {onboarding.criteria.map((c) => (
            <li key={c.code} className="flex items-start gap-2 text-sm">
              {c.met ? (
                <Check size={15} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-label="fait" />
              ) : (
                <Circle size={15} className="mt-0.5 shrink-0 text-desk-faint" aria-label="à faire" />
              )}
              <span className={c.met ? 'text-desk-soft' : 'text-desk-strong'}>
                {c.label}
                {!c.required && <span className="text-desk-muted"> (facultatif)</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {!onboarding && <p className="mt-2 text-xs text-desk-muted">Critères de l'étape illisibles pour l'instant.</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {stage < STABLE_ONBOARDING_STAGE && (
          <button type="button" disabled={busy} onClick={() => onAdvance(stage)} className={BTN_SECONDARY}>
            {busy && <Loader2 size={15} className="animate-spin" aria-hidden="true" />}
            {stage <= 0 ? "Démarrer l'onboarding" : 'Étape suivante'}
          </button>
        )}
        {stage >= 2 && !reopening && (
          <button type="button" disabled={busy} onClick={() => setReopening(true)} className={BTN_GHOST}>
            Revenir à une étape…
          </button>
        )}
      </div>
      {reopening && (
        <Reopen
          stage={stage}
          busy={busy}
          onReopen={(to, reason) => void onReopen(to, reason).then((ok) => ok && setReopening(false))}
          onCancel={() => setReopening(false)}
        />
      )}
      <dl className="mt-4 grid grid-cols-[88px_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
        {facts
          .filter(([, value]) => value)
          .map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-desk-muted">{label}</dt>
              <dd className="m-0 min-w-0 text-desk-strong">{value}</dd>
            </div>
          ))}
      </dl>
      {(checklists.client || checklists.internal) && (
        <div className="mt-4">
          <div className="flex gap-4">
            {checklists.client && <Tally label="Checklist client" tally={checklists.client} />}
            {checklists.internal && <Tally label="Checklist interne" tally={checklists.internal} />}
          </div>
          <Link to={leadFicheHref(row.id)} onClick={onNavigate} className="mt-2 inline-block text-sm font-medium text-desk-accent-text hover:underline">
            Voir les checklists
          </Link>
        </div>
      )}
    </SheetBlock>
  );
}
